import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const PlaybookCtx = createContext(null);
const keyOf = (qid, fid) => `${qid}:${fid || ""}`;

const fileInto = async (item, folderId, name) => {
  try {
    await api.post(`/playbook/${item.id}/folders/${folderId}`);
    toast.success(`Filed in "${name}"`);
  } catch (e) { toast.error(errMsg(e)); }
};

export const suggestFolder = (item) => {
  const s = item.suggestion;
  if (!s) return toast.success("Saved to your playbook");
  if (s.folder_id) {
    return toast.success("Saved to your playbook", {
      description: `Looks like it belongs in "${s.name}".`, duration: 8000,
      action: { label: `Add to ${s.name}`, onClick: () => fileInto(item, s.folder_id, s.name) },
    });
  }
  toast.success("Saved to your playbook", {
    description: `Start a "${s.new_name}" folder for it?`, duration: 8000,
    action: {
      label: `Create "${s.new_name}"`,
      onClick: async () => {
        try {
          const { data } = await api.post("/playbook/folders", { name: s.new_name });
          await fileInto(item, data.id, s.new_name);
        } catch (e) { toast.error(errMsg(e)); }
      },
    },
  });
};

export const PlaybookProvider = ({ children }) => {
  const [saved, setSaved] = useState({});
  useEffect(() => {
    api.get("/playbook").then((r) => setSaved(Object.fromEntries(r.data.map((i) => [keyOf(i.question_id, i.followup_id), i.id])))).catch(() => {});
  }, []);
  const toggle = useCallback(async (qid, fid) => {
    const k = keyOf(qid, fid);
    try {
      if (saved[k]) {
        await api.delete(`/playbook/${saved[k]}`);
        setSaved((s) => { const n = { ...s }; delete n[k]; return n; });
        toast.success("Removed from your playbook");
      } else {
        const { data } = await api.post("/playbook", { question_id: qid, followup_id: fid || null });
        setSaved((s) => ({ ...s, [k]: data.id }));
        suggestFolder(data);
      }
    } catch (e) { toast.error(errMsg(e)); }
  }, [saved]);
  return <PlaybookCtx.Provider value={{ saved, toggle }}>{children}</PlaybookCtx.Provider>;
};

export const SaveButton = ({ qid, fid, testId }) => {
  const ctx = useContext(PlaybookCtx);
  if (!ctx) return null;
  const on = !!ctx.saved[keyOf(qid, fid)];
  const Icon = on ? BookmarkCheck : Bookmark;
  return (
    <Button size="sm" variant="ghost" onClick={() => ctx.toggle(qid, fid)} data-testid={testId}
      className={`rounded-full text-xs ${on ? "text-pinkb" : "text-muted-foreground"}`}>
      <Icon className="h-4 w-4 mr-1.5" />{on ? "Saved to playbook" : "Save to playbook"}
    </Button>
  );
};
